import React, { useState, useEffect } from 'react';
import { Box, Stepper, Step, StepLabel } from '@mui/material';
import Modal from '../../../../../../common/Modal';
import Assignment from './components/Assignment';
import TeamSelection from './components/TeamSelection';
import RolesSelection from './components/RolesSelection';
import TransferDetails from './components/TransferDetails';
import DefaultInternalAssignment from './components/DefaultInternalAssignment';
import AttachmentTypeSelection, { ATTACHMENT_TYPES } from './components/AttachmentTypeSelection';
import BenchDetailsStep from './components/BenchDetailsStep';
import { projectsApi, organizationApi } from '../../../../../../../services/api';
import LoadingSpinner from '../../../../../../common/LoadingSpinner';
import { ROLE_TYPES } from '../../../../../../../constants/roleTypes';
import { PROJECT_PURPOSE_TYPES } from '../../../../../../../constants/projectPurposes';
import { EMPLOYEE_ENGAGEMENT_TYPES } from '../../../../../../../constants/employeeEngagementTypes';
import { BENCH_TYPES } from '../../../../../../../constants/benchTypes';
import { useAbility } from '../../../../../../../contexts/AbilityContext';
import { ACTIONS, SUBJECTS } from '../../../../../../../constants/permissions';
import { toLocalDay } from '../../../../../../../utils/dateOnly';

const getSteps = (isTransfer, isDefaultInternal, attachmentType, isBench, canWriteBenchData) => {
  if (isDefaultInternal && !isTransfer) {
    if (attachmentType === ATTACHMENT_TYPES.TRANSFER) {
      return ['Attachment Type', 'Assignment', 'Team Selection', 'Roles Selection'];
    }
    return ['Attachment Type', 'Individual & Representative Selection'];
  }

  const baseSteps = isTransfer
    ? ['Transfer Details', 'Assignment', 'Team Selection', 'Roles Selection']
    : ['Assignment', 'Team Selection', 'Roles Selection'];

  // Add Bench Details step only when the target is a bench project AND caller has write permission
  if (isBench && canWriteBenchData) {
    return [...baseSteps, 'Bench Details'];
  }

  return baseSteps;
};

const EmployeeTransfer = ({
  isOpen,
  onClose,
  projectId,
  projectData,
  onSubmit,
  employeeId,
  employees
}) => {
  const ability = useAbility();
  const canWriteBenchData = ability.can(ACTIONS.CREATE, SUBJECTS.BENCH_EMPLOYEE_DATA);

  const [currentStep, setCurrentStep] = useState(1);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedEngagementType, setSelectedEngagementType] = useState('');
  const [currentEngagementType, setCurrentEngagementType] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [isStartDateManuallySet, setIsStartDateManuallySet] = useState(false);
  const [endDate, setEndDate] = useState(null);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [selectedFunctionalRoles, setSelectedFunctionalRoles] = useState([]);
  const [selectedTechnicalRoles, setSelectedTechnicalRoles] = useState([]);
  const [selectedPositionForRoles, setSelectedPositionForRoles] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedDestinationProject, setSelectedDestinationProject] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);
  const [attachmentType, setAttachmentType] = useState(null);
  const [benchType, setBenchType] = useState(BENCH_TYPES.BENCH);
  const [flagAsStar, setFlagAsStar] = useState(false);
  const [benchComments, setBenchComments] = useState('');
  const [benchManager, setBenchManager] = useState(null);
  const [benchSalary, setBenchSalary] = useState('');
  const [benchSalaryCurrency, setBenchSalaryCurrency] = useState('');
  const [benchSalaryPeriod, setBenchSalaryPeriod] = useState('');
  const [benchSalaryAmountType, setBenchSalaryAmountType] = useState('');

  // In transfer mode, projectId is the source project
  const projectSourceId = employeeId ? projectId : null;
  // In attach mode, projectId is the destination project
  const projectDestinationId = !employeeId ? projectId : null;

  // Check if the project is DEFAULT_INTERNAL
  const isDefaultInternal = projectData?.projectPurpose?.code === PROJECT_PURPOSE_TYPES.DEFAULT_INTERNAL;
  // Check if the TARGET project is a bench project
  // In transfer mode: check the selected destination project
  // In attach mode: the current project IS the destination
  const isBenchProject = employeeId
    ? selectedDestinationProject?.projectPurpose?.code === PROJECT_PURPOSE_TYPES.BENCH
    : projectData?.projectPurpose?.code === PROJECT_PURPOSE_TYPES.BENCH;

  const steps = getSteps(!!employeeId, isDefaultInternal, attachmentType, isBenchProject, canWriteBenchData);

  useEffect(() => {
    if (!isOpen) {
      // Reset step and team selection
      setCurrentStep(1);
      setSelectedTeamId(null);
      setSelectedFunctionalRoles([]);
      setSelectedTechnicalRoles([]);
      setEndDate(null);
      setIsLoading(false);
      setSelectedDestinationProject(null);
      setIsStartDateManuallySet(false);
      setAttachmentType(null);
      setSelectedManager(null);
      setBenchType(BENCH_TYPES.BENCH);
      setFlagAsStar(false);
      setBenchComments('');
      setBenchManager(null);
      setBenchSalary('');
      setBenchSalaryCurrency('');
      setBenchSalaryPeriod('');
      setBenchSalaryAmountType('');

      // Only reset employee-related data if not in transfer mode
      if (!employeeId) {
        setSelectedEmployee(null);
        setSelectedEngagementType('');
        setStartDate(null);
        setSelectedPositionForRoles(null);
      }
    }
  }, [isOpen, employeeId]);

  // Add effect to handle employee selection in attach mode
  useEffect(() => {
    if (!employeeId && selectedEmployee) {
      // Set position for roles selection in attach mode
      setSelectedPositionForRoles(selectedEmployee.position);
    }
  }, [employeeId, selectedEmployee]);

  // Add effect to set the employee and their current roles when opening the modal for transfer
  useEffect(() => {
    const fetchEmployeeProjectData = async () => {
      if (isOpen && employeeId && projectId) {
        try {
          setIsLoading(true);
          // Fetch employee's specific project data
          const employeesData = await projectsApi.getProjectEmployees(projectId);
          const employee = employeesData?.find(emp => emp.id === employeeId && emp.projectEndDate === null);
          
          if (employee) {
            setSelectedEmployee(employee);
            setCurrentEngagementType(employee.engagementType);
            
            if (employee.roles) {
              const functionalRoles = employee.roles
                .filter(role => role.type === ROLE_TYPES.FUNCTIONAL)
                .map(role => role.id);
              const technicalRoles = employee.roles
                .filter(role => role.type === ROLE_TYPES.TECHNICAL)
                .map(role => role.id);
              
              setSelectedFunctionalRoles(functionalRoles);
              setSelectedTechnicalRoles(technicalRoles);
            }
            
            // Always set position from the employee data for roles filtering
            setSelectedPositionForRoles(employee.position);
          }
        } catch (error) {
          console.error('Error fetching employee project data:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchEmployeeProjectData();
  }, [isOpen, employeeId, projectId]);

  // Update the effect to respect manual changes
  useEffect(() => {
    if (employeeId && endDate && !isStartDateManuallySet) {
      // Get the next working day after the end date
      let nextDay = toLocalDay(endDate).add(1, 'day');
      
      // Skip weekends
      while (nextDay.day() === 0 || nextDay.day() === 6) {
        nextDay = nextDay.add(1, 'day');
      }
      
      setStartDate(nextDay);
    }
  }, [employeeId, endDate, isStartDateManuallySet]);

  const handleModalClose = () => {
    onClose();
  };

  const handleBack = () => {
    if (currentStep > 2) {
      setCurrentStep(currentStep - 1);
    } else if (currentStep === 2) {
      setCurrentStep(1);
      // Only reset destination project when in attach mode
      if (!employeeId) {
        setSelectedDestinationProject(null);
      }
      // Reset attachment type when going back to step 1 for DEFAULT_INTERNAL
      if (isDefaultInternal && !employeeId) {
        setAttachmentType(null);
        setSelectedEmployee(null);
        setSelectedManager(null);
        setSelectedEngagementType('');
        setStartDate(null);
      }
    }
  };

  const handleSubmit = () => {
    if (employeeId) {
      // Transfer flow (from menu on existing employee)
      if (currentStep === 1) {
        // Only set the destination engagement type if it hasn't been set yet
        if (!selectedEngagementType && currentEngagementType?.id) {
          setSelectedEngagementType(currentEngagementType.id);
        }
        setCurrentStep(2);
      } else if (currentStep < steps.length) {
        setCurrentStep(currentStep + 1);
      } else {
        const submitData = {
          // Source assignment data
          source: {
            projectId: projectSourceId,
            employeeId: selectedEmployee.id,
            endDate: endDate
          },
          // Destination assignment data
          destination: {
            projectId: selectedDestinationProject?.id,
            employeeId: selectedEmployee.id,
            engagementTypeId: selectedEngagementType || null,
            startDate: startDate,
            teamId: selectedTeamId,
            roleIds: [...selectedFunctionalRoles, ...selectedTechnicalRoles]
          }
        };

        // Include bench data when transferring to a bench project and caller has write permission
        if (isBenchProject && canWriteBenchData) {
          submitData.destination.benchType = benchType;
          submitData.destination.flagAsStar = flagAsStar;
          submitData.destination.comments = benchComments || null;
          submitData.destination.benchManagerId = benchManager?.id || null;
          submitData.destination.salary = benchSalary || null;
          submitData.destination.salaryCurrency = benchSalaryCurrency || null;
          submitData.destination.salaryPeriod = benchSalaryPeriod || null;
          submitData.destination.salaryAmountType = benchSalaryAmountType || null;
        }

        onSubmit(submitData);
      }
    } else if (isDefaultInternal && !employeeId) {
      // DEFAULT_INTERNAL attach flow
      if (currentStep === 1) {
        // Step 1: Attachment type selection - move to step 2
        setCurrentStep(2);
      } else if (attachmentType === ATTACHMENT_TYPES.ATTACH) {
        // Attach flow - submit after Employee & Manager Selection (step 2)
        onSubmit({
          isDefaultInternal: true,
          businessUnitId: projectData?.businessUnit?.id,
          employeeId: selectedEmployee?.id,
          managerId: selectedManager?.id
        });
      } else if (attachmentType === ATTACHMENT_TYPES.TRANSFER) {
        // Transfer flow for DEFAULT_INTERNAL - follow regular attach steps
        if (currentStep === 2) {
          setCurrentStep(3);
        } else if (currentStep === 3) {
          setCurrentStep(4);
        } else {
          // Step 4: Submit with regular attach format
          onSubmit({
            source: {
              projectId: null,
              employeeId: null,
              endDate: null
            },
            destination: {
              projectId: projectDestinationId,
              employeeId: selectedEmployee.id,
              engagementTypeId: selectedEngagementType || null,
              startDate: startDate,
              teamId: selectedTeamId,
              roleIds: [...selectedFunctionalRoles, ...selectedTechnicalRoles]
            }
          });
        }
      }
    } else {
      // Regular attach flow (including bench projects)
      if (currentStep < steps.length) {
        setCurrentStep(currentStep + 1);
      } else {
        const submitData = {
          // Source assignment data
          source: {
            projectId: null,
            employeeId: null,
            endDate: null
          },
          // Destination assignment data
          destination: {
            projectId: projectDestinationId,
            employeeId: selectedEmployee.id,
            engagementTypeId: selectedEngagementType || null,
            startDate: startDate,
            teamId: selectedTeamId,
            roleIds: [...selectedFunctionalRoles, ...selectedTechnicalRoles]
          }
        };

        // Include bench data when attaching to a bench project and caller has write permission
        if (isBenchProject && canWriteBenchData) {
          submitData.destination.benchType = benchType;
          submitData.destination.flagAsStar = flagAsStar;
          submitData.destination.comments = benchComments || null;
          submitData.destination.benchManagerId = benchManager?.id || null;
          submitData.destination.salary = benchSalary || null;
          submitData.destination.salaryCurrency = benchSalaryCurrency || null;
          submitData.destination.salaryPeriod = benchSalaryPeriod || null;
          submitData.destination.salaryAmountType = benchSalaryAmountType || null;
        }

        onSubmit(submitData);
      }
    }
  };

  const getStepContent = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <LoadingSpinner size={40} />
        </Box>
      );
    }

    if (employeeId) {
      // Transfer flow
      switch (currentStep) {
        case 1:
          return (
            <TransferDetails
              selectedEmployee={selectedEmployee}
              selectedProject={selectedProject}
              setSelectedProject={setSelectedProject}
              endDate={endDate}
              setEndDate={setEndDate}
              projectId={projectSourceId}
              currentEngagementType={currentEngagementType}
            />
          );
        case 2:
          return (
            <Assignment
              selectedEmployee={selectedEmployee}
              setSelectedEmployee={setSelectedEmployee}
              selectedEngagementType={selectedEngagementType}
              setSelectedEngagementType={setSelectedEngagementType}
              startDate={startDate}
              setStartDate={(date) => {
                setIsStartDateManuallySet(true);
                setStartDate(date);
              }}
              projectId={projectDestinationId}
              employeeId={employeeId}
              selectedDestinationProject={selectedDestinationProject}
              onProjectSelect={(project) => {
                setSelectedDestinationProject(project);
              }}
            />
          );
        case 3:
          return (
            <TeamSelection
              projectId={selectedDestinationProject?.id}
              selectedTeamId={selectedTeamId}
              setSelectedTeamId={setSelectedTeamId}
              selectedEmployee={selectedEmployee}
            />
          );
        case 4:
          return (
            <RolesSelection
              selectedPositionForRoles={selectedPositionForRoles}
              setSelectedPositionForRoles={setSelectedPositionForRoles}
              selectedFunctionalRoles={selectedFunctionalRoles}
              setSelectedFunctionalRoles={setSelectedFunctionalRoles}
              selectedTechnicalRoles={selectedTechnicalRoles}
              setSelectedTechnicalRoles={setSelectedTechnicalRoles}
              initialPosition={selectedEmployee?.position}
            />
          );
        case 5:
          // Bench Details step (only reached when destination is a bench project)
          if (isBenchProject) {
            return (
              <BenchDetailsStep
                benchType={benchType}
                setBenchType={setBenchType}
                flagAsStar={flagAsStar}
                setFlagAsStar={setFlagAsStar}
                comments={benchComments}
                setComments={setBenchComments}
                benchManager={benchManager}
                setBenchManager={setBenchManager}
                directManager={selectedEmployee?.directManager || null}
                salary={benchSalary}
                setSalary={setBenchSalary}
                salaryCurrency={benchSalaryCurrency}
                setSalaryCurrency={setBenchSalaryCurrency}
                salaryPeriod={benchSalaryPeriod}
                setSalaryPeriod={setBenchSalaryPeriod}
                salaryAmountType={benchSalaryAmountType}
                setSalaryAmountType={setBenchSalaryAmountType}
              />
            );
          }
          return null;
        default:
          return null;
      }
    } else {
      // Attach flow
      if (isDefaultInternal) {
        // DEFAULT_INTERNAL flow with attachment type selection
        switch (currentStep) {
          case 1:
            // Step 1: Attachment type selection
            return (
              <AttachmentTypeSelection
                selectedType={attachmentType}
                onTypeSelect={setAttachmentType}
              />
            );
          case 2:
            if (attachmentType === ATTACHMENT_TYPES.ATTACH) {
              // Attach flow - Employee & Manager Selection
              return (
                <DefaultInternalAssignment
                  selectedEmployee={selectedEmployee}
                  setSelectedEmployee={setSelectedEmployee}
                  selectedManager={selectedManager}
                  setSelectedManager={setSelectedManager}
                  setSelectedPositionForRoles={setSelectedPositionForRoles}
                />
              );
            } else {
              // Transfer flow - Assignment step
              return (
                <Assignment
                  selectedEmployee={selectedEmployee}
                  setSelectedEmployee={setSelectedEmployee}
                  selectedEngagementType={selectedEngagementType}
                  setSelectedEngagementType={setSelectedEngagementType}
                  startDate={startDate}
                  setStartDate={setStartDate}
                  projectId={projectDestinationId}
                  disabledEngagementTypeCodes={[EMPLOYEE_ENGAGEMENT_TYPES.FULL_TIME]}
                />
              );
            }
          case 3:
            // Transfer flow - Team Selection
            return (
              <TeamSelection
                projectId={projectDestinationId}
                selectedTeamId={selectedTeamId}
                setSelectedTeamId={setSelectedTeamId}
                selectedEmployee={selectedEmployee}
              />
            );
          case 4:
            // Transfer flow - Roles Selection
            return (
              <RolesSelection
                selectedPositionForRoles={selectedPositionForRoles}
                setSelectedPositionForRoles={setSelectedPositionForRoles}
                selectedFunctionalRoles={selectedFunctionalRoles}
                setSelectedFunctionalRoles={setSelectedFunctionalRoles}
                selectedTechnicalRoles={selectedTechnicalRoles}
                setSelectedTechnicalRoles={setSelectedTechnicalRoles}
                initialPosition={selectedEmployee?.position}
              />
            );
          default:
            return null;
        }
      }

      // Regular attach flow (including bench projects)
      switch (currentStep) {
        case 1:
          return (
            <Assignment
              selectedEmployee={selectedEmployee}
              setSelectedEmployee={setSelectedEmployee}
              selectedEngagementType={selectedEngagementType}
              setSelectedEngagementType={setSelectedEngagementType}
              startDate={startDate}
              setStartDate={setStartDate}
              projectId={projectDestinationId}
            />
          );
        case 2:
          return (
            <TeamSelection
              projectId={projectDestinationId}
              selectedTeamId={selectedTeamId}
              setSelectedTeamId={setSelectedTeamId}
              selectedEmployee={selectedEmployee}
            />
          );
        case 3:
          return (
            <RolesSelection
              selectedPositionForRoles={selectedPositionForRoles}
              setSelectedPositionForRoles={setSelectedPositionForRoles}
              selectedFunctionalRoles={selectedFunctionalRoles}
              setSelectedFunctionalRoles={setSelectedFunctionalRoles}
              selectedTechnicalRoles={selectedTechnicalRoles}
              setSelectedTechnicalRoles={setSelectedTechnicalRoles}
              initialPosition={selectedEmployee?.position}
            />
          );
        case 4:
          // Bench Details step (only reached when isBenchProject is true)
          if (isBenchProject) {
            return (
              <BenchDetailsStep
                benchType={benchType}
                setBenchType={setBenchType}
                flagAsStar={flagAsStar}
                setFlagAsStar={setFlagAsStar}
                comments={benchComments}
                setComments={setBenchComments}
                benchManager={benchManager}
                setBenchManager={setBenchManager}
                directManager={selectedEmployee?.directManager || null}
                salary={benchSalary}
                setSalary={setBenchSalary}
                salaryCurrency={benchSalaryCurrency}
                setSalaryCurrency={setBenchSalaryCurrency}
                salaryPeriod={benchSalaryPeriod}
                setSalaryPeriod={setBenchSalaryPeriod}
                salaryAmountType={benchSalaryAmountType}
                setSalaryAmountType={setBenchSalaryAmountType}
              />
            );
          }
          return null;
        default:
          return null;
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleModalClose}
      title={employeeId ? "Transfer Individual" : "Attach Individual"}
      onSubmit={handleSubmit}
      submitText={
        currentStep === steps.length
          ? (employeeId ? "Transfer Individual" : "Attach Individual")
          : "Next"
      }
      submitDisabled={
        isDefaultInternal && !employeeId
          ? (currentStep === 1
              ? !attachmentType
              : attachmentType === ATTACHMENT_TYPES.ATTACH
                ? (!selectedEmployee || !selectedManager)
                : (currentStep === 2
                    ? (!selectedEmployee || !selectedEngagementType || !startDate)
                    : currentStep === 3
                      ? !selectedTeamId
                      : false))
          : currentStep === 1
            ? (employeeId ? !endDate : (!selectedEmployee || !selectedEngagementType || !startDate))
            : currentStep === 2 && employeeId
              ? (!selectedDestinationProject || !selectedEngagementType || !startDate)
              : currentStep === (employeeId ? 3 : 2)
                ? !selectedTeamId
                : false
      }
      cancelText={currentStep === 1 ? "Cancel" : "Back"}
      onCancel={currentStep === 1 ? handleModalClose : handleBack}
      submitButtonProps={{
        sx: { backgroundColor: '#2563EB', color: '#FFFFFF', '&:hover': { backgroundColor: '#1E40AF' } }
      }}
      cancelButtonProps={{
        sx: { backgroundColor: '#F1F5F9', color: '#475569', '&:hover': { backgroundColor: '#E2E8F0' } }
      }}
      width={
        employeeId
          ? currentStep === 4  // Roles selection in transfer mode
            ? '1200px'
            : '800px'
          : isDefaultInternal
            ? (attachmentType === ATTACHMENT_TYPES.TRANSFER && currentStep === 4)
              ? '1200px'  // Roles selection in DEFAULT_INTERNAL transfer mode
              : '600px'   // Wider modal for DEFAULT_INTERNAL flow
            : currentStep === 3  // Roles selection in regular attach mode
              ? '1200px'
              : isBenchProject
                ? '700px'  // Extra room for bench attach stepper
                : (currentStep === 1 && selectedEmployee)
                  ? '600px'
                  : '500px'
      }
    >
      <Box sx={{ width: '100%', mb: 2 }}>
        <Stepper activeStep={currentStep - 1}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Box sx={{ '& > *': { gap: 2 } }}>
        {getStepContent()}
      </Box>
    </Modal>
  );
};

export default EmployeeTransfer; 